import "./AppPayWorkplaces.css";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { CheckWorkplace, Workspace, Address, Company, CheckCompany, RequestErrorCode, isCheckError, CheckResponseError } from "winslow-core";
import { 
    apiCheckListCompanyWorkplaces, 
    apiCheckUpdateWorkplace,
    apiCheckCreateWorkplace,
} from "../../../services/PayrollService";
import { Button, Icon, Alert, AlertDescription } from "../../../ds2/components";
import { Plus, Edit, Power, PowerOff } from "lucide-react";
import { ActionLink } from "../../../ds2/components/ActionLink";
import { AppAddress } from "../app-address/AppAddress";
import ModalConfirm from "../../policy-studio/components/ModalConfirm";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { AppCheckErrorMessage } from "./AppCheckErrorMessage";

type AppPayWorkplacesProps = {
  company: Company | null;
  checkCompany: CheckCompany | null;
};

export const AppPayWorkplaces = ({ company, checkCompany }: AppPayWorkplacesProps) => {
    const dispatch = useDispatch();

    const uiStates = useSelector(selectUIStates);
    const storeUser = useSelector(selectUser);

    const checkCompanyId = company?.checkCompanyId || checkCompany?.id;

    const [checkWorkplaces, setCheckWorkplaces] = useState<CheckWorkplace[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [showAddForm, setShowAddForm] = useState<boolean>(false);
    const [editingCheckWorkplace, setEditingCheckWorkplace] = useState<CheckWorkplace | null>(null);
    const [workplaceToggleActivation, setWorkplaceToggleActivation] = useState<CheckWorkplace | null>(null);
    const [showToggleActivationConfirm, setShowToggleActivationConfirm] = useState<boolean>(false);
    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);

    const [formData, setFormData] = useState<Omit<CheckWorkplace, "id">>({
        name: "",
        address: {
            line1: "",
            line2: "",
            postal_code: "",
            city: "",
            state: "CA",
            country: "",
        } as Address,
        company: checkCompanyId || "",
        active: true,
        metadata: {},
    });

    // Sort workplaces alphabetically by name
    const sortedWorkplaces = useMemo(() => {
        return [...checkWorkplaces].sort((a, b) => 
            (a.name || "").localeCompare(b.name || "")
        );
    }, [checkWorkplaces]);

    useEffect(() => {
        dispatch(setUIStates({...uiStates, hrisLocation: "Payroll Configuration"}));
        loadCheckWorkplaces();
    }, [storeUser, checkCompanyId]);

    const loadCheckWorkplaces = async () => {
        if (!checkCompanyId) return;
        
        setLoading(true);
        try {

            const apiResponse = await apiCheckListCompanyWorkplaces(checkCompanyId);
            const workplaces = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !workplaces) {
                toast("Error loading workplaces", toastFail);
                return;
            }

            setCheckWorkplaces(workplaces || []);
        } catch (error) {
            console.error("[AppPayWorkplaces] Error loading workplaces:", error);
            toast("Error loading workplaces", toastFail);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: "",
            address: {
                line1: "",
                line2: "",
                postal_code: "",
                city: "",
                state: "CA",
                country: "",
            },
            company: checkCompanyId || "",
            active: true,
            metadata: {},
        });
        setEditingCheckWorkplace(null);
        setShowAddForm(false);
        setCheckResponseError(undefined);
    };

    const handleAddClick = () => {
        resetForm();
        setShowAddForm(true);
    };

    const handleEditClick = (workplace: CheckWorkplace) => {
        setFormData({
            name: workplace.name || "",
            address: workplace.address || {
                line1: "",
                line2: "",
                postal_code: "",
                city: "",
                state: "CA",
                country: "",
            },
            company: workplace.company || checkCompanyId || "",
            active: workplace.active !== undefined ? workplace.active : true,
            metadata: workplace.metadata || {},
        });
        setEditingCheckWorkplace(workplace);
        setShowAddForm(true);
    };

    const handleToggleActivationClick = (workplace: CheckWorkplace) => {
        setWorkplaceToggleActivation(workplace);
        setShowToggleActivationConfirm(true);
    };

    const handleSave = async () => {
        setCheckResponseError(undefined);
        
        if (!checkCompanyId) {
            toast("Check company ID not found", toastFail);
            return;
        }

        if (!formData.name.trim()) {
            toast("Name is required", toastFail);
            return;
        }

        if (!formData.address.postal_code?.trim()) {
            toast("Postal code is required", toastFail);
            return;
        }

        const payload: CheckWorkplace = {
            id: editingCheckWorkplace?.id || "", 
            name: formData.name.trim(),
            address: formData.address as Address,
            company: checkCompanyId || "",
            active: editingCheckWorkplace?.active !== undefined ? editingCheckWorkplace.active : formData.active,
            metadata: editingCheckWorkplace?.metadata || formData.metadata || {},
        };

    
        if (editingCheckWorkplace) {
            const apiResponse = await apiCheckUpdateWorkplace(editingCheckWorkplace.id, payload);
           
            if (!apiResponse) {
                toast("Error updating workplace", toastFail);
                setCheckResponseError(undefined)
                return;
            }

            setCheckResponseError(apiResponse.checkResponseError);  // set or clear

            if (isCheckError(apiResponse)) {
                const errorMsg = apiResponse.checkResponseError?.errorMessage || "Error updating workplace";
                toast(`Error updating workplace (${errorMsg})`, toastFail);
            }

            const updatedCheckWorkplace = apiResponse.data;

            if (updatedCheckWorkplace) {
                toast("Workplace updated successfully", toastSuccess);
                await loadCheckWorkplaces();
                resetForm();
            } else {
                toast("Error updating workplace", toastFail);
            }
        } else {
            const apiResponse = await apiCheckCreateWorkplace(checkCompanyId, payload);

            if (!apiResponse) {
                toast("Error creating workplace", toastFail);
                return;
            }

            setCheckResponseError(apiResponse.checkResponseError);  // set or clear

            if (isCheckError(apiResponse)) {
                const errorMsg = apiResponse.checkResponseError?.errorMessage || "Error creating workplace";
                toast(`Error creating workplace (${errorMsg})`, toastFail);
                return;
            }

            const createdCheckWorkplace = apiResponse.data;

            if (createdCheckWorkplace) {
                toast("Workplace created successfully", toastSuccess);
                await loadCheckWorkplaces();
                resetForm();
            } else {
                toast("Error creating workplace", toastFail);
            }
        }
    };

    const handleToggleActivationConfirm = async () => {
        if (!workplaceToggleActivation || !checkCompanyId) return;

        const newActiveStatus = !workplaceToggleActivation.active;
        const payload: CheckWorkplace = {
            id: workplaceToggleActivation.id,
            name: workplaceToggleActivation.name,
            address: workplaceToggleActivation.address,
            company: checkCompanyId,
            active: newActiveStatus,
            metadata: workplaceToggleActivation.metadata || {},
        };

        try {
            const apiResponse = await apiCheckUpdateWorkplace(workplaceToggleActivation.id, payload);

            if (!apiResponse) {
                toast("Error updating workplace status", toastFail);
                return;
            }

            setCheckResponseError(apiResponse.checkResponseError);  // set or clear

            if (isCheckError(apiResponse)) {
                const errorMsg = apiResponse.checkResponseError?.errorMessage || "Error updating workplace status";
                toast(`Error updating workplace status (${errorMsg})`, toastFail);
                return;
            }

            const updatedCheckWorkplace = apiResponse.data;
            if (updatedCheckWorkplace) {
                toast(
                    `Workplace ${newActiveStatus ? "activated" : "deactivated"} successfully`,
                    toastSuccess
                );
                await loadCheckWorkplaces();
            } else {
                toast("Error updating workplace status", toastFail);
            }
            
        } catch (error) {
            console.error("[AppPayWorkplaces] Error toggling workplace activation:", error);
            toast("Error updating workplace status", toastFail);
        } finally {
            setShowToggleActivationConfirm(false);
            setWorkplaceToggleActivation(null);
        }
    };

    const formatAddress = (address: Address | undefined): string => {
        if (!address) return "-";
        const parts = [
            address.line1,
            address.line2,
            address.city,
            address.state,
            address.postal_code,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(", ") : "-";
    };

    return (
        <div className="app-workplaces">
            <AppCheckErrorMessage checkResponseError={checkResponseError} />
            <div className="app-workplaces-header">
                {!showAddForm && (
                    <ActionLink
                        label="Add Workplace"
                        leftIcon={<Plus size={16} />}
                        onClick={handleAddClick}
                    />
                )}
            </div>

            {showAddForm && (
                <div className="app-workplaces-form">
                    <div className="app-workplaces-form-header">
                        <h3>{editingCheckWorkplace ? "Edit Workplace" : "Add Workplace"}</h3>
                    </div>
                    <div className="app-workplaces-form-content">
                        <div className="app-custom-property-label">
                            Name *
                        </div>
                        <div className="app-custom-property-value">
                            <input
                                type="text"
                                placeholder="Enter workplace name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div className="app-custom-property-label">
                            Address
                        </div>
                        <div className="app-custom-property-value">
                            <AppAddress
                                value={formData.address}
                                onChange={(address) => setFormData({ ...formData, address })}
                            />
                        </div>
                    </div>
                    <div className="app-workplaces-form-actions">
                        <Button
                            label="Cancel"
                            variant="secondary"
                            onClick={resetForm}
                        />
                        <Button
                            label={editingCheckWorkplace ? "Update" : "Create"}
                            variant="primary"
                            onClick={handleSave}
                            disabled={!formData.name.trim() || !formData.address.postal_code?.trim()}
                        />
                    </div>
                </div>
            )}

            {!showAddForm && (
                <>
                    {loading ? (
                        <div className="app-workplaces-loading">
                            Loading workplaces...
                        </div>
                    ) : sortedWorkplaces.length === 0 ? (
                        <div className="app-workplaces-empty">
                            <p>No workplaces found. Click "Add Workplace" to create one.</p>
                        </div>
                    ) : (
                        <div className="app-workplaces-table-container">
                            <table className="app-workplaces-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Address</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedWorkplaces.map((workplace) => (
                                        <tr key={workplace.id} className="app-workplaces-row">
                                            <td className="app-workplaces-name-cell">
                                                <strong>{workplace.name || "-"}</strong>
                                            </td>
                                            <td className="app-workplaces-address-cell">
                                                {formatAddress(workplace.address)}
                                            </td>
                                            <td className="app-workplaces-status-cell">
                                                <span className={`app-workplaces-status ${workplace.active ? "active" : "inactive"}`}>
                                                    {workplace.active ? "Active" : "Inactive"}
                                                </span>
                                            </td>
                                            <td className="app-workplaces-actions-cell">
                                                <div className="app-workplaces-actions-group">
                                                    <Icon
                                                        variant="tertiary"
                                                        size="md"
                                                        icon={<Edit size={16} />}
                                                        onClick={() => handleEditClick(workplace)}
                                                        title="Edit"
                                                    />
                                                    <Icon
                                                        variant="tertiary"
                                                        size="md"
                                                        icon={workplace.active ? <PowerOff size={16} /> : <Power size={16} />}
                                                        onClick={() => handleToggleActivationClick(workplace)}
                                                        title={workplace.active ? "Deactivate" : "Activate"}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            <ModalConfirm
                title={workplaceToggleActivation?.active ? "Deactivate Workplace" : "Activate Workplace"}
                text={workplaceToggleActivation ? 
                    `Are you sure you want to ${workplaceToggleActivation.active ? "deactivate" : "activate"} "${workplaceToggleActivation.name}"?` : 
                    ""
                }
                isOpen={showToggleActivationConfirm}
                onClose={() => {
                    setShowToggleActivationConfirm(false);
                    setWorkplaceToggleActivation(null);
                }}
                onContinue={handleToggleActivationConfirm}
            />
        </div>
    );
};